/**
 * Renders the lock, setup, or account-ended screen OVER the app.
 *
 * Overlays rather than routes on purpose. Navigating to a lock route would
 * unmount the screen underneath, losing in-progress visit documentation every
 * time a nurse's phone sat in her pocket for ten minutes. An absolutely
 * positioned cover keeps that state alive while making it unreachable.
 */
import React from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";

import { AccountEndedScreen } from "@/components/app-lock/AccountEndedScreen";
import { AppLockSetup } from "@/components/app-lock/AppLockSetup";
import { LockScreen } from "@/components/app-lock/LockScreen";
import { useAppLock } from "@/lib/app-lock-context";
import { colors } from "@/lib/theme";

export function AppLockGate() {
    const { screen, accountEndedReason } = useAppLock();

    if (screen === "unlocked") {
        return null;
    }

    return (
        <View style={styles.cover} pointerEvents="auto">
            {screen === "account-ended" ? (
                <AccountEndedScreen reason={accountEndedReason ?? "terminated"} />
            ) : screen === "locked" ? (
                <LockScreen />
            ) : screen === "initializing" ? (
                // Opaque, content-free hold while we read the secure store.
                // Deliberately not the lock screen: we do not yet know whether
                // a PIN exists, and prompting for one that was never set would
                // strand the nurse.
                <View style={styles.initializing}>
                    <ActivityIndicator size="large" color={colors.teal400} />
                </View>
            ) : (
                <AppLockSetup />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    cover: {
        ...StyleSheet.absoluteFill,
        // Above every in-app surface including the recording pill and modals.
        zIndex: 9999,
        elevation: 9999
    },
    initializing: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.gray50
    }
});
